/**
 * Gobodo List View Service
 *
 * CRUD for ListView__c records.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { handleGobodoError, logGobodoError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { serializeGobodoData } from '../providers/gobodoRoleProvider';
import { objectNameToApiName } from './gobodoTabConfigService';
import type {
  ListView,
  ListViewColumn,
  FilterCondition,
  SortConfig,
} from '../queries/listViewQueries';

const OBJECT_KEY = 'ListView__c';

function toIsoString(val: any): string {
  if (val == null) return new Date().toISOString();
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && typeof val.toISOString === 'function') return val.toISOString();
  return new Date().toISOString();
}

function parseJson<T>(val: any, fallback: T): T {
  if (val == null) return fallback;
  if (typeof val === 'string' && val) {
    try {
      return JSON.parse(val) as T;
    } catch {
      return fallback;
    }
  }
  if (Array.isArray(val)) return val as T;
  return fallback;
}

function gobodoToListView(record: any): ListView {
  const raw = record.properties || record.data || {};
  const data = serializeGobodoData(raw) as Record<string, any>;
  const columns = parseJson<ListViewColumn[]>(data.columnsJson, []);
  const filters = parseJson<FilterCondition[]>(data.filtersJson, []);
  const sort = parseJson<SortConfig[]>(data.sortJson, []);
  return {
    id: String(record.id),
    name: data.name || '',
    objectName: data.objectTypeKey || '',
    description: data.description || undefined,
    columns,
    filters,
    sort,
    isDefault: Boolean(data.isDefault),
    isShared: Boolean(data.isShared),
    createdBy: String(record.creatorid || data.createdBy || 'system'),
    createdDate: toIsoString(record.creationdate ?? data.createdAt),
    lastModified: toIsoString(record.modificationdate ?? data.updatedAt),
  };
}

function listViewToGobodoProps(view: Partial<ListView>): Record<string, any> {
  return {
    objectTypeKey: view.objectName || '',
    name: view.name || '',
    description: view.description ?? '',
    columnsJson: view.columns ? JSON.stringify(view.columns) : '[]',
    filtersJson: view.filters ? JSON.stringify(view.filters) : '[]',
    sortJson: view.sort ? JSON.stringify(view.sort) : '[]',
    isDefault: view.isDefault ?? false,
    isShared: view.isShared ?? false,
    createdBy: view.createdBy || 'system',
    updatedBy: view.createdBy || 'system',
  };
}

export async function fetchListViews(objectTypeKey: string): Promise<ListView[]> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const apiName = objectNameToApiName(objectTypeKey);
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const allRecords = await retryWithBackoff(
    () =>
      objectsApi.getObjectsInGraphByType(GobodoConfig.graph(), OBJECT_KEY, { page: 0, size: 100 }),
    { maxRetries: 2 }
  );

  const records = (allRecords || []).filter((r: any) => {
    const p = serializeGobodoData(r.properties || r.data || {}) as Record<string, any>;
    return (p.objectTypeKey || '') === apiName;
  });

  return records
    .map((r: any) => gobodoToListView(r))
    .sort((a: ListView, b: ListView) => a.lastModified.localeCompare(b.lastModified));
}

export async function fetchListView(id: string): Promise<ListView> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const record = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(id)), {
    maxRetries: 3,
  });
  return gobodoToListView(record);
}

export async function createListView(
  data: Omit<ListView, 'id' | 'createdBy' | 'createdDate' | 'lastModified'>
): Promise<ListView> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  await ensureObjectTypeExists(OBJECT_KEY, {
    label: 'List View',
    pluralLabel: 'List Views',
    description: 'List view configuration (columns, filters, sort) per object',
    icon: 'Table',
    category: 'system',
  });

  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  const objectType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEY, { maxFields: 0 }), {
    maxRetries: 3,
  });

  const apiName = objectNameToApiName(data.objectName);
  const props = listViewToGobodoProps({ ...data, objectName: apiName, createdBy: 'Current User' });

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEY;
  payload.typeid = objectType.id as any;
  payload.label = `${apiName} - ${data.name}`;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = props;

  const created = await retryWithBackoff(() => objectsApi.createObject(payload), { maxRetries: 3 });
  return gobodoToListView(created);
}

export async function updateListView(
  id: string,
  updates: Partial<Pick<ListView, 'name' | 'description' | 'columns' | 'filters' | 'sort' | 'isDefault' | 'isShared'>>
): Promise<ListView> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const existing = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(id)), {
    maxRetries: 3,
  });
  const current = gobodoToListView(existing);

  const merged: ListView = { ...current, ...updates };
  const props = listViewToGobodoProps(merged);

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEY;
  payload.typeid = (existing as any).typeid;
  payload.label = existing.label;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = { ...(existing.properties || existing.data || {}), ...props };

  const updated = await retryWithBackoff(() => objectsApi.updateObject(parseInt(id), payload), {
    maxRetries: 3,
  });
  return gobodoToListView(updated);
}

export async function deleteListView(id: string): Promise<void> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    await retryWithBackoff(() => objectsApi.deleteObject(parseInt(id)), { maxRetries: 3 });
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'deleteListView', objectId: id });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}
